import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Tabs,
  Tab,
  Badge
} from 'react-bootstrap';
import { Mail, Server, Send, FileText, CheckCircle, AlertTriangle, Save } from 'react-feather';
import SettingsService from '../../../services/SettingsService';

const EmailSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_secure: false,
    smtp_user: '',
    smtp_pass: '',
    smtp_from_email: '',
    smtp_from_name: ''
  });
  const [templates, setTemplates] = useState({
    welcome: { subject: '', body: '' },
    invoice: { subject: '', body: '' },
    renewal_reminder: { subject: '', body: '' },
    cancel: { subject: '', body: '' },
    payment_failed: { subject: '', body: '' }
  });
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [smtpResponse, templatesResponse] = await Promise.all([
        SettingsService.getSmtpSettings(),
        SettingsService.getEmailTemplates()
      ]);

      if (smtpResponse.success) {
        setSmtpSettings(prev => ({
          ...prev,
          ...smtpResponse.settings,
          smtp_secure: smtpResponse.settings.smtp_secure === 'true'
        }));
      }

      if (templatesResponse.success) {
        setTemplates(templatesResponse.templates);
      }
    } catch (err) {
      setError('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmtpSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTemplateChange = (templateName, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateName]: {
        ...prev[templateName],
        [field]: value
      }
    }));
  };

  const handleSmtpSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await SettingsService.updateSmtpSettings({
        ...smtpSettings,
        smtp_secure: smtpSettings.smtp_secure.toString()
      });
      
      if (response.success) {
        setSuccess('SMTP settings saved successfully!');
      } else {
        setError(response.error || 'Failed to save settings');
      }
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateSubmit = async (templateName) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await SettingsService.updateEmailTemplate(
        templateName,
        templates[templateName]
      );
      
      if (response.success) {
        setSuccess(`Template "${templateName}" saved successfully!`);
      } else {
        setError(response.error || 'Failed to save template');
      }
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await SettingsService.testSmtp(testEmail);
      
      if (response.success) {
        setSuccess(`Test email sent to ${testEmail} successfully!`);
      } else {
        setError(response.error || 'Failed to send test email');
      }
    } catch (err) {
      setError(err.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const templateVariables = {
    welcome: ['user_name', 'user_email'],
    invoice: ['plan_name', 'amount', 'period_start', 'period_end', 'invoice_date'],
    renewal_reminder: ['renewal_date', 'plan_name', 'amount'],
    cancel: ['end_date', 'plan_name'],
    payment_failed: ['plan_name', 'amount']
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading settings...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <Mail className="me-2" size={28} />
          Email & Notification Settings
        </h2>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          <AlertTriangle size={18} className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
          <CheckCircle size={18} className="me-2" />
          {success}
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        <Tab eventKey="smtp" title={<><Server size={16} className="me-2" />SMTP Configuration</>}>
          <Row>
            <Col lg={8}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">SMTP Server Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSmtpSubmit}>
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>SMTP Host</Form.Label>
                          <Form.Control
                            type="text"
                            name="smtp_host"
                            value={smtpSettings.smtp_host}
                            onChange={handleSmtpChange}
                            placeholder="smtp.gmail.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Port</Form.Label>
                          <Form.Control
                            type="number"
                            name="smtp_port"
                            value={smtpSettings.smtp_port}
                            onChange={handleSmtpChange}
                            placeholder="587"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="smtp_secure"
                        label="Use TLS/SSL"
                        checked={smtpSettings.smtp_secure}
                        onChange={handleSmtpChange}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Username</Form.Label>
                          <Form.Control
                            type="text"
                            name="smtp_user"
                            value={smtpSettings.smtp_user}
                            onChange={handleSmtpChange}
                            placeholder="your-email@gmail.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="smtp_pass"
                            value={smtpSettings.smtp_pass}
                            onChange={handleSmtpChange}
                            placeholder="•••••••• or app password"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>From Email</Form.Label>
                          <Form.Control
                            type="email"
                            name="smtp_from_email"
                            value={smtpSettings.smtp_from_email}
                            onChange={handleSmtpChange}
                            placeholder="noreply@yourdomain.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>From Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="smtp_from_name"
                            value={smtpSettings.smtp_from_name}
                            onChange={handleSmtpChange}
                            placeholder="Your App Name"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2">
                      <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <Spinner size="sm" /> : <><Save size={16} className="me-2" />Save Settings</>}
                      </Button>
                    </div>
                  </Form>

                  <hr className="my-4" />

                  <h6>Test Configuration</h6>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="email"
                      placeholder="Enter test email address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                    <Button 
                      variant="outline-primary" 
                      onClick={handleTestEmail}
                      disabled={testing}
                    >
                      {testing ? <Spinner size="sm" /> : <><Send size={16} className="me-2" />Test</>}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="bg-light">
                <Card.Header>
                  <h5 className="mb-0">Common SMTP Settings</h5>
                </Card.Header>
                <Card.Body className="small">
                  <p><strong>Gmail:</strong><br/>
                  Host: smtp.gmail.com<br/>
                  Port: 587<br/>
                  Use App Password</p>

                  <p><strong>Outlook:</strong><br/>
                  Host: smtp.office365.com<br/>
                  Port: 587</p>

                  <p><strong>SendGrid:</strong><br/>
                  Host: smtp.sendgrid.net<br/>
                  Port: 587</p>

                  <p><strong>Mailgun:</strong><br/>
                  Host: smtp.mailgun.org<br/>
                  Port: 587</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="templates" title={<><FileText size={16} className="me-2" />Email Templates</>}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Email Templates</h5>
            </Card.Header>
            <Card.Body>
              <Tabs defaultActiveKey="welcome" className="mb-3">
                {Object.entries(templates).map(([key, template]) => (
                  <Tab 
                    key={key} 
                    eventKey={key} 
                    title={key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  >
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          type="text"
                          value={template.subject}
                          onChange={(e) => handleTemplateChange(key, 'subject', e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Body</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={10}
                          value={template.body}
                          onChange={(e) => handleTemplateChange(key, 'body', e.target.value)}
                          style={{ fontFamily: 'monospace' }}
                        />
                      </Form.Group>

                      <div className="mb-3">
                        <small className="text-muted">Available variables: </small>
                        {templateVariables[key].map((variable, idx) => (
                          <Badge key={variable} bg="secondary" className="me-1">
                            {`{{${variable}}}`}
                          </Badge>
                        ))}
                      </div>

                      <Button 
                        variant="primary" 
                        onClick={() => handleTemplateSubmit(key)}
                        disabled={saving}
                      >
                        {saving ? <Spinner size="sm" /> : <><Save size={16} className="me-2" />Save Template</>}
                      </Button>
                    </Form>
                  </Tab>
                ))}
              </Tabs>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user
});

export default connect(mapStateToProps)(EmailSettings);
